import { ChevronDown, Search, X } from 'lucide-react'
import { memo, useMemo, useState } from 'react'

import { COMMON } from '@brickup/shared/locales/de/common'
import { CONTROLS } from '@brickup/shared/locales/de/controls'
import {
  Button,
  cn,
  Field,
  FOCUS_RING,
  Modal,
  ModalFooter,
  TextInput,
  useDisabled,
} from '@/components/base'
import {
  ICON_GROUP_IDS,
  ICON_GROUP_NAMES,
  ICON_NAMES,
  ICON_REGISTRY,
  resolveIcon,
  type IconGroupId,
} from '@/constants/icon-registry'

/**
 * German search terms and the English substrings they should match.
 *
 * lucide names everything in English, so without this a search for "Dusche"
 * finds nothing — and the same table is what reads an entry's own title for
 * the suggestions the dialog opens on.
 */
const GERMAN_ICON_ALIASES: Record<string, string[]> = {
  tür: ['door'],
  tuer: ['door'],
  fenster: ['window', 'blinds'],
  wand: ['brick', 'wallpaper', 'layers'],
  wände: ['brick', 'wallpaper', 'layers'],
  boden: ['footprints', 'layers', 'grid'],
  bodenbelag: ['footprints', 'layers', 'grid'],
  decke: ['layers', 'lampceiling'],
  dach: ['house', 'home'],
  dämmung: ['layers', 'brick'],
  heizung: ['heater', 'thermometer', 'flame'],
  heizkörper: ['heater', 'thermometer'],
  lüftung: ['fan', 'airvent', 'wind'],
  klima: ['airvent', 'fan', 'snowflake'],
  bad: ['bath', 'shower', 'toilet', 'droplet'],
  badezimmer: ['bath', 'shower', 'toilet'],
  dusche: ['shower', 'droplet'],
  wanne: ['bath'],
  wc: ['toilet'],
  armatur: ['shower', 'droplet', 'pipette'],
  armaturen: ['shower', 'droplet', 'pipette'],
  sanitär: ['bath', 'shower', 'toilet', 'droplet'],
  küche: ['utensils', 'chef', 'cook', 'refrigerator'],
  kueche: ['utensils', 'chef', 'cook', 'refrigerator'],
  schlafzimmer: ['bed'],
  wohnzimmer: ['sofa', 'armchair'],
  esszimmer: ['utensils', 'chef'],
  keller: ['warehouse', 'archive'],
  garage: ['car', 'warehouse'],
  garten: ['tree', 'sprout', 'leaf', 'flower'],
  strom: ['zap', 'plug', 'power', 'cable', 'lightbulb'],
  elektrik: ['zap', 'plug', 'cable', 'power'],
  elektro: ['zap', 'plug', 'cable', 'power'],
  steckdose: ['plug', 'power'],
  wasser: ['droplet', 'waves', 'shower', 'bath'],
  licht: ['lightbulb', 'lamp', 'sun'],
  beleuchtung: ['lightbulb', 'lamp', 'sun'],
  lampe: ['lamp', 'lightbulb'],
  moebel: ['sofa', 'armchair', 'bed'],
  möbel: ['sofa', 'armchair', 'bed'],
  bett: ['bed'],
  treppe: ['stairs', 'footprints'],
  auto: ['car', 'caravan'],
  fahrrad: ['bike'],
  werkzeug: ['hammer', 'wrench', 'drill', 'construction'],
  malerarbeiten: ['paint', 'brush', 'roller'],
  fliesen: ['grid', 'layers'],
  dokument: ['file', 'folder', 'clipboard'],
  geld: ['banknote', 'coins', 'wallet', 'euro'],
  ziegel: ['brick'],
  fassade: ['brick', 'building'],
  photovoltaik: ['sun', 'zap', 'battery'],
  solar: ['sun', 'zap'],
}

/** How many icons a title may suggest — one screenful of the grid, no more. */
const MAX_SUGGESTIONS = 12

/** `DropletOff` is the negation of what was asked for, `BathOff` likewise —
 *  a suggestion has to mean the thing, not its absence. */
const isNegated = (name: string) => name.endsWith('Off')

const matchesNeedle = (name: string, needle: string) => {
  const lower = name.toLowerCase()
  if (lower.includes(needle)) return true
  return (GERMAN_ICON_ALIASES[needle] ?? []).some(alias =>
    lower.includes(alias),
  )
}

/**
 * The icons an entry's own name asks for: "Armaturen unterputz" offers the
 * shower head and the droplet before anyone has typed anything.
 */
function suggestionsFor(title: string | undefined): string[] {
  if (!title) return []
  const words = title
    .toLowerCase()
    .split(/[^a-zäöüß]+/)
    .filter(Boolean)
  const hits: string[] = []
  for (const word of words) {
    for (const alias of GERMAN_ICON_ALIASES[word] ?? []) {
      // The plain glyph before its variants: `Droplet` says "water", while
      // `Droplets` says something narrower.
      const candidates = ICON_NAMES.filter(
        name => !isNegated(name) && name.toLowerCase().includes(alias),
      ).sort((a, b) => a.length - b.length)
      for (const name of candidates) {
        if (hits.length >= MAX_SUGGESTIONS) return hits
        if (!hits.includes(name)) hits.push(name)
      }
    }
  }
  return hits
}

/** The rail's entries: what the title suggests, everything, then the groups. */
const SUGGESTED = 'suggested'
const ALL = 'all'
type RailId = typeof SUGGESTED | typeof ALL | IconGroupId

const IconTile = memo(function IconTile({
  name,
  isSelected,
  onSelect,
}: {
  name: string
  isSelected: boolean
  onSelect: (name: string) => void
}) {
  const Glyph = ICON_REGISTRY[name]
  if (!Glyph) return null

  return (
    <button
      type="button"
      onClick={() => onSelect(name)}
      className={cn(
        'flex flex-col items-center gap-1.5 rounded-lg border px-1 py-2.5',
        FOCUS_RING,
        isSelected
          ? 'border-primary bg-selected text-primary'
          : 'border-transparent text-foreground-2 hover:bg-line-soft',
      )}
    >
      <Glyph size={22} />
      <span
        className={cn(
          'max-w-full truncate font-mono text-[10.5px]',
          isSelected ? 'text-primary' : 'text-faint',
        )}
      >
        {name}
      </span>
    </button>
  )
})

/**
 * The icon catalogue as a dialog: categories on the left, named glyphs on the
 * right, one search across both.
 *
 * Names under the glyphs rather than in a tooltip — the stored value *is* the
 * name, and 376 unlabelled 18-px symbols are not something anyone browses.
 * What the entry's own title suggests heads the rail, so the everyday case is
 * the first thing on screen.
 */
function IconPickerDialog({
  open,
  onClose,
  value,
  onChange,
  suggestFrom,
  emptyHint,
}: {
  open: boolean
  onClose: () => void
  value: string | null
  onChange: (icon: string | null) => void
  /** The entry's own title; its words are what the suggestions are read off. */
  suggestFrom?: string
  /** What the entry wears while no icon is picked, where the caller can name
   *  something better than the generic sentence — clearing is otherwise a step
   *  into the dark. */
  emptyHint?: string
}) {
  const [query, setQuery] = useState('')
  const suggestions = useMemo(() => suggestionsFor(suggestFrom), [suggestFrom])
  const [rail, setRail] = useState<RailId>(
    suggestions.length > 0 ? SUGGESTED : ALL,
  )

  // The search runs over the open category, so narrowing by hand and by word
  // compose rather than fight: "Bad & Sanitär" plus "shower" is two steps to
  // four icons.
  const shown = useMemo(() => {
    const names =
      rail === SUGGESTED
        ? suggestions
        : rail === ALL
          ? ICON_NAMES
          : ICON_GROUP_NAMES[rail]
    const needle = query.trim().toLowerCase()
    return needle ? names.filter(name => matchesNeedle(name, needle)) : names
  }, [query, rail, suggestions])

  const railEntries: { id: RailId; label: string; count: number }[] = [
    ...(suggestions.length > 0
      ? [
          {
            id: SUGGESTED as RailId,
            label: CONTROLS.iconPicker.suggestions,
            count: suggestions.length,
          },
        ]
      : []),
    { id: ALL, label: CONTROLS.iconPicker.all, count: ICON_NAMES.length },
    ...ICON_GROUP_IDS.map(id => ({
      id: id as RailId,
      label: CONTROLS.iconPicker.groups[id],
      count: ICON_GROUP_NAMES[id].length,
    })),
  ]

  const select = (name: string | null) => {
    onChange(name)
    onClose()
  }

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={CONTROLS.iconPicker.title}
      size="lg"
      contentClassName="h-[34rem]"
      bodyClassName="flex min-h-0 flex-1 flex-col p-0"
      footer={
        <ModalFooter
          leading={
            <div className="flex items-center gap-3">
              <Button variant="ghost" size="sm" onClick={() => select(null)}>
                {CONTROLS.iconPicker.clear}
              </Button>
              <span className="text-xs text-faint">
                {emptyHint ?? CONTROLS.iconPicker.emptyFallback}
              </span>
            </div>
          }
          onCancel={onClose}
          cancelLabel={COMMON.close}
        />
      }
    >
      <div className="border-b border-line-soft px-4 py-3">
        <TextInput
          autoFocus
          value={query}
          onValueChange={next => setQuery(next ?? '')}
          placeholder={CONTROLS.iconPicker.searchPlaceholder}
        />
      </div>

      <div className="flex min-h-0 flex-1 flex-row">
        <div className="flex w-48 shrink-0 flex-col gap-0.5 overflow-y-auto border-r border-line-soft p-2">
          {railEntries.map(entry => (
            <button
              key={entry.id}
              type="button"
              onClick={() => setRail(entry.id)}
              className={cn(
                'flex flex-row items-center justify-between gap-2 rounded-md px-2.5 py-1 text-left',
                'text-[12.5px] transition-colors',
                rail === entry.id
                  ? 'bg-selected font-semibold text-primary'
                  : 'text-foreground-2 hover:bg-line-soft',
              )}
            >
              <span className="truncate">{entry.label}</span>
              <span
                className={cn(
                  'shrink-0 tabular-nums',
                  rail === entry.id ? 'text-primary' : 'text-faint',
                )}
              >
                {entry.count}
              </span>
            </button>
          ))}
        </div>

        <div className="min-w-0 flex-1 overflow-y-auto p-3">
          {shown.length === 0 ? (
            <p className="py-10 text-center text-xs text-muted-foreground">
              {CONTROLS.iconPicker.noResults}
            </p>
          ) : (
            <div className="grid grid-cols-[repeat(auto-fill,minmax(6rem,1fr))] gap-1.5">
              {shown.map(name => (
                <IconTile
                  key={name}
                  name={name}
                  isSelected={value === name}
                  onSelect={select}
                />
              ))}
            </div>
          )}
        </div>
      </div>
    </Modal>
  )
}

/**
 * The dialog plus the state that opens it.
 *
 * Returned rather than rendered, so the trigger can be anything: the entry's
 * own mark in a pane's identity strip, a button in a table row, a labelled
 * field in a form.
 */
export function useIconPicker(args: {
  value: string | null
  onChange: (icon: string | null) => void
  suggestFrom?: string
  emptyHint?: string
}) {
  const [open, setOpen] = useState(false)

  return {
    open: () => setOpen(true),
    // Nothing while shut: the dialog's body scans the icon catalogue for
    // suggestions on every render of the pane it hangs in, for a list most
    // sessions never open.
    dialog: open ? (
      <IconPickerDialog {...args} open onClose={() => setOpen(false)} />
    ) : null,
  }
}

/**
 * The stored icon of an entry, where no other surface carries it: a glyph
 * button in a table row, or a labelled field in a form.
 *
 * A detail pane uses neither — there the entry's own mark in the identity
 * strip opens the dialog, and a second copy of it in the form below said the
 * same thing twice.
 */
export function IconPicker({
  value,
  onChange,
  suggestFrom,
  emptyHint,
  variant = 'field',
  label,
}: {
  value: string | null
  onChange: (icon: string | null) => void
  suggestFrom?: string
  /** What the entry wears while nothing is picked. See `useIconPicker`. */
  emptyHint?: string
  /** `glyph` is the square-button form for a table row, where a full-width
   *  field would not fit and the glyph alone says what is set. */
  variant?: 'field' | 'glyph'
  /** Set where the form around it labels its rows; a table row leaves it off. */
  label?: string
}) {
  const picker = useIconPicker({ value, onChange, suggestFrom, emptyHint })
  const Glyph = resolveIcon(value)
  const disabled = useDisabled()

  if (variant === 'glyph') {
    return (
      <>
        <button
          type="button"
          aria-label={CONTROLS.iconPicker.title}
          onClick={picker.open}
          disabled={disabled}
          className={cn(
            'flex h-7 w-7 items-center justify-center rounded-md border transition-colors',
            'disabled:pointer-events-none disabled:opacity-40',
            FOCUS_RING,
            value
              ? 'border-primary/40 bg-accent-soft text-primary'
              : 'border-border text-faint hover:bg-line-soft',
          )}
        >
          <Glyph size={15} />
        </button>
        {picker.dialog}
      </>
    )
  }

  const control = (
    <>
      <button
        type="button"
        onClick={picker.open}
        disabled={disabled}
        className={cn(
          'flex h-8 w-full flex-row items-center gap-2 rounded-md border border-border px-2',
          'disabled:pointer-events-none disabled:opacity-40',
          'bg-card text-left transition-colors hover:border-primary/40',
          FOCUS_RING,
        )}
      >
        <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded bg-accent-soft text-primary">
          {value ? <Glyph size={15} /> : null}
        </span>
        <span
          className={cn(
            'flex-1 truncate text-[13px]',
            value ? 'font-mono text-[12px] text-foreground-2' : 'text-faint',
          )}
        >
          {value ?? CONTROLS.iconPicker.empty}
        </span>
        {value ? (
          <span
            role="button"
            tabIndex={-1}
            aria-label={CONTROLS.iconPicker.clear}
            onClick={event => {
              event.stopPropagation()
              onChange(null)
            }}
            className="rounded-md p-0.5 text-faint hover:bg-line-soft hover:text-destructive"
          >
            <X size={13} />
          </span>
        ) : null}
        <Search size={13} className="shrink-0 text-faint" />
        <ChevronDown size={13} className="shrink-0 text-faint" />
      </button>
      {picker.dialog}
    </>
  )

  return label ? <Field label={label}>{() => control}</Field> : control
}
